import { issueStationSession } from '@jq4race/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type CachedStationSession,
  cacheSession,
  clearStationDevice,
  getCachedSession,
  getDeviceToken,
  setDeviceToken,
} from '../lib/station-session-store';

/**
 * Translatable reason a session mint failed. The UI resolves it to copy via
 * the station namespace (`errors.<key>`) — the raw backend message is never
 * shown (it is English-only and not meant for end users).
 */
export type StationErrorKey =
  | 'invalid_station_token'
  | 'rate_limited'
  | 'network_error'
  | 'unknown';

function toErrorKey(err: unknown): StationErrorKey {
  const { code, status } = (err ?? {}) as { code?: unknown; status?: unknown };
  if (code === 'invalid_station_token' || code === 'rate_limited' || code === 'network_error') {
    return code;
  }
  // A 401 on POST /station/session can only mean the device token was
  // rejected, even when the body carried no (or a generic) code.
  if (status === 401) return 'invalid_station_token';
  return 'unknown';
}

export interface UseStationSessionResult {
  /** null while no device token has been entered yet — the caller shows the setup form. */
  deviceToken: string | null;
  /** null while minting/loading, or after a mint failure — the caller shows a loading/error state. */
  session: CachedStationSession | null;
  loading: boolean;
  error: StationErrorKey | null;
  /**
   * Called from the one-time setup form. Validates the token by minting a
   * session; the token is persisted only if that succeeds, so a bad paste
   * leaves the device unpaired and the setup form on screen.
   */
  submitDeviceToken: (token: string) => void;
  /** Forgets the device entirely (token + session) — returns to the setup screen. */
  forgetDevice: () => void;
  /** Re-attempts minting a session with the currently stored device token. */
  retry: () => void;
}

/**
 * Orchestrates the station device's session lifecycle: if a device token is
 * stored, ensure a valid (non-expired) session exists, minting one via
 * POST /station/session when missing/expired. Never touches the operator
 * auth-store/session — see station-session-store.ts's header comment.
 */
export function useStationSession(): UseStationSessionResult {
  const [deviceToken, setDeviceTokenState] = useState<string | null>(() => getDeviceToken());
  const [session, setSession] = useState<CachedStationSession | null>(() => getCachedSession());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StationErrorKey | null>(null);
  // Bumped to force a re-mint attempt of the stored token (retry()).
  const [attempt, setAttempt] = useState(0);
  // Lets the effect see a freshly minted session even when localStorage could
  // not persist it (so a successful submit doesn't mint a second time).
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const mint = useCallback(async (token: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await issueStationSession(token);
      const cached = cacheSession(response);
      setSession(cached);
      return true;
    } catch (err) {
      setSession(null);
      setError(toErrorKey(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is an intentional re-run trigger (retry bumps it), not a value read in the effect body
  useEffect(() => {
    if (!deviceToken) return;
    const cached = getCachedSession();
    if (cached) {
      setSession(cached);
      return;
    }
    const live = sessionRef.current;
    if (live && live.expiresAt > Date.now()) return;
    void mint(deviceToken);
  }, [deviceToken, attempt, mint]);

  const submitDeviceToken = useCallback(
    (token: string) => {
      const trimmed = token.trim();
      if (!trimmed) return;
      // Validate BEFORE persisting: a rejected paste must not become the
      // device's stored credential, otherwise "Retry" would re-send the same
      // bad token forever. On failure deviceToken stays null, so the caller
      // keeps showing the setup form (with the error) for another attempt.
      void mint(trimmed).then((ok) => {
        if (!ok) return;
        setDeviceToken(trimmed);
        setDeviceTokenState(trimmed);
      });
    },
    [mint],
  );

  const forgetDevice = useCallback(() => {
    clearStationDevice();
    setDeviceTokenState(null);
    setSession(null);
    setError(null);
  }, []);

  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  return { deviceToken, session, loading, error, submitDeviceToken, forgetDevice, retry };
}
